import { useCallback, useEffect, useMemo, useState } from "react";

import { FirebaseAuth } from "../components/FirebaseAuth/firebase";
import { isContractorApproved, isContractorReviewPending } from "../libs/contractorReview";
import { buildMonthlySeries, formatMoney } from "../libs/dashboardOverview";
import { normalizeContractDoc } from "../libs/firestoreSchema";
import {
  buildDisplayName,
  formatCurrency,
  formatDate,
  isEmailLike,
  normalizeError,
  statusColor,
} from "../pages/auth/admin/Dashboard/adminDashboardUtils";

const readField = (row = {}, snakeKey, fallback = undefined) => {
  if (row && Object.prototype.hasOwnProperty.call(row, snakeKey)) return row[snakeKey];
  return fallback;
};

const normalizeProfileDoc = (id, data = {}) => ({
  id,
  ...data,
  connectAccountId: readField(data, "connect_account_id", ""),
  displayName: readField(data, "display_name", ""),
  needsRefresh: Boolean(readField(data, "needs_refresh", false)),
  generatedResume: readField(data, "generated_resume", ""),
  generatedResumeHtml: readField(data, "generated_resume_html", ""),
  experienceSummary: readField(data, "experience_summary", []),
  domainFocus: readField(data, "domain_focus", []),
  sourceStats: readField(data, "source_stats", {}),
  aiMeta: readField(data, "ai_meta", {}),
  updatedAt: readField(data, "updated_at", null),
});

const normalizePricingRequest = (id, data = {}) => ({
  id,
  ...data,
  requestId: readField(data, "request_id", id),
  uid: readField(data, "uid", ""),
  currentHourlyCents: Number(readField(data, "current_hourly_cents", 0) || 0),
  requestedHourlyCents: Number(readField(data, "requested_hourly_cents", 0) || 0),
  currentService: readField(data, "current_service", ""),
  currentLevel: readField(data, "current_level", ""),
  requestedService: readField(data, "requested_service", ""),
  requestedLevel: readField(data, "requested_level", ""),
  requestedCurrency: readField(data, "requested_currency", data.currency || "usd"),
  adminReason: readField(data, "admin_reason", ""),
  resolvedBy: readField(data, "resolved_by", ""),
  resolvedAt: readField(data, "resolved_at", null),
  effectiveDate: readField(data, "effective_date", ""),
  effectiveFrom: readField(data, "effective_from", null),
  createdAt: readField(data, "created_at", null),
  updatedAt: readField(data, "updated_at", null),
});

const useAdminDashboard = () => {
  const adminUid = FirebaseAuth.auth().currentUser?.uid || "";
  const authUser = FirebaseAuth.auth().currentUser;
  const initialAdminDisplayName = String(authUser?.displayName || "").trim() || "Admin";
  const initialAdminPhotoURL = String(authUser?.photoURL || "").trim();

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [adminProfile, setAdminProfile] = useState({
    displayName: initialAdminDisplayName,
    photoURL: initialAdminPhotoURL,
  });
  const [contracts, setContracts] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [pricingRequests, setPricingRequests] = useState([]);
  const [openTimesheets, setOpenTimesheets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [accountsCount, setAccountsCount] = useState(0);
  const [busyById, setBusyById] = useState({});
  const [userDirectory, setUserDirectory] = useState({});
  const [contractorProfilesByUid, setContractorProfilesByUid] = useState({});

  const setBusy = (id, value) => {
    setBusyById((prev) => ({ ...prev, [id]: value }));
  };

  const loadAdminData = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const db = FirebaseAuth.firestore();
      const listOpenTimesheets = FirebaseAuth.functions().httpsCallable("adminListOpenTimesheets");
      const listInvoices = FirebaseAuth.functions().httpsCallable("adminListContractorInvoices");
      const [contractsRes, usersRes, accountsRes, pendingAdminRequestsRes, adminsRes, profilesRes, timesheetsRes, invoicesRes, adminSelfRes] = await Promise.allSettled([
        db.collection("contracts").get(),
        db.collection("users").where("role", "==", "contractor").get(),
        db.collection("accounts").get(),
        db.collection("admin_requests").where("status", "==", "pending").get(),
        db.collection("users").where("role", "==", "admin").get(),
        db.collection("profile").get(),
        listOpenTimesheets({}),
        listInvoices({}),
        adminUid ? db.collection("users").doc(adminUid).get() : Promise.resolve(null),
      ]);

      if (adminSelfRes.status === "fulfilled" && adminSelfRes.value?.exists) {
        const adminData = adminSelfRes.value.data() || {};
        const storedName = buildDisplayName(adminData);
        setAdminProfile({
          displayName: storedName && storedName !== "User" ? storedName : initialAdminDisplayName,
          photoURL: String(adminData.photo_url || initialAdminPhotoURL || "").trim(),
        });
      } else {
        setAdminProfile({
          displayName: initialAdminDisplayName,
          photoURL: initialAdminPhotoURL,
        });
      }

      const accountNameById = {};
      if (accountsRes.status === "fulfilled") {
        accountsRes.value.docs.forEach((doc) => {
          const a = doc.data() || {};
          const resolved = [
            String(a.name || "").trim(),
            String(a.company_name || "").trim(),
            String(a?.company?.name || "").trim(),
          ].find((v) => v && !isEmailLike(v));
          if (resolved) accountNameById[doc.id] = resolved;
        });
      }

      if (contractsRes.status === "fulfilled") {
        const mapped = contractsRes.value.docs.map((d) => {
          const c = normalizeContractDoc(d.id, d.data() || {});
          const accountId = String(c.accountId || "").trim();
          const accountName = String(c.accountName || accountNameById[accountId] || "").trim();
          const companyName = [
            String(c.companyName || "").trim(),
            accountName,
          ].find((v) => v && !isEmailLike(v)) || "";

          return {
            ...c,
            accountId,
            accountName: accountName || c.accountName || "",
            companyName: companyName || c.companyName || "",
          };
        });
        mapped.sort((a, b) => {
          const aSec = Number(a.updatedAt?.seconds || a.updatedAt?._seconds || a.createdAt?.seconds || a.createdAt?._seconds || 0);
          const bSec = Number(b.updatedAt?.seconds || b.updatedAt?._seconds || b.createdAt?.seconds || b.createdAt?._seconds || 0);
          return bSec - aSec;
        });
        setContracts(mapped);
      } else {
        setContracts([]);
      }

      if (usersRes.status === "fulfilled") {
        setContractors(usersRes.value.docs.map((d) => ({ uid: d.id, ...(d.data() || {}) })));
      } else {
        setContractors([]);
      }

      setAccountsCount(accountsRes.status === "fulfilled" ? accountsRes.value.size || 0 : 0);

      if (pendingAdminRequestsRes.status === "fulfilled") {
        const rows = pendingAdminRequestsRes.value.docs
          .map((d) => normalizePricingRequest(d.id, d.data() || {}))
          .filter((row) => String(row.type || "").toLowerCase() === "pricing_update");
        rows.sort((a, b) => {
          const aSec = Number(a.updatedAt?.seconds || a.updatedAt?._seconds || a.createdAt?.seconds || a.createdAt?._seconds || 0);
          const bSec = Number(b.updatedAt?.seconds || b.updatedAt?._seconds || b.createdAt?.seconds || b.createdAt?._seconds || 0);
          return bSec - aSec;
        });
        setPricingRequests(rows);
      } else {
        setPricingRequests([]);
      }

      if (timesheetsRes.status === "fulfilled") {
        const rowsRaw = Array.isArray(timesheetsRes.value?.data?.timesheets)
          ? timesheetsRes.value.data.timesheets
          : [];
        const rows = rowsRaw.map((row) => {
          const userId = String(row.user_id || "").trim();
          return {
            ...row,
            key: `${userId}:${row.id || ""}`,
            userId,
            totalHours: row.total_hours,
            totalCost: row.total_cost,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
          };
        });
        rows.sort((a, b) => {
          const aSec = Number(a.updatedAt?.seconds || a.updatedAt?._seconds || a.createdAt?.seconds || 0);
          const bSec = Number(b.updatedAt?.seconds || b.updatedAt?._seconds || b.createdAt?.seconds || 0);
          return bSec - aSec;
        });
        setOpenTimesheets(rows);
      } else {
        setOpenTimesheets([]);
      }

      if (invoicesRes.status === "fulfilled") {
        const rowsRaw = Array.isArray(invoicesRes.value?.data?.invoices)
          ? invoicesRes.value.data.invoices
          : [];
        setInvoices(rowsRaw.map((row) => {
          const userId = String(row.user_id || "").trim();
          return {
            ...row,
            key: `${userId}:${row.id || ""}`,
            invoiceId: row.invoice_id,
            timesheetId: row.timesheet_id,
            userId,
            hoursWorked: row.hours_worked,
            createdAt: row.created_at,
            dueDate: row.due_date,
            updatedAt: row.updated_at,
          };
        }));
      } else {
        setInvoices([]);
      }

      if (profilesRes.status === "fulfilled") {
        const map = {};
        profilesRes.value.docs.forEach((doc) => {
          const profile = normalizeProfileDoc(doc.id, doc.data() || {});
          const aliases = [
            doc.id,
            profile.uid,
          ].map((value) => String(value || "").trim()).filter(Boolean);
          aliases.forEach((alias) => {
            map[alias] = profile;
          });
        });
        setContractorProfilesByUid(map);
      } else {
        setContractorProfilesByUid({});
      }

      const directory = {};
      if (usersRes.status === "fulfilled") {
        usersRes.value.docs.forEach((d) => {
          directory[d.id] = buildDisplayName(d.data() || {});
        });
      }
      if (adminsRes.status === "fulfilled") {
        adminsRes.value.docs.forEach((d) => {
          directory[d.id] = buildDisplayName(d.data() || {});
        });
      }

      const lookupUids = new Set();
      if (contractsRes.status === "fulfilled") {
        contractsRes.value.docs.forEach((docSnap) => {
          const c = normalizeContractDoc(docSnap.id, docSnap.data() || {});
          if (c.clientUid) lookupUids.add(String(c.clientUid));
          if (c.contractorUid) lookupUids.add(String(c.contractorUid));
          if (Array.isArray(c.statusHistory)) {
            c.statusHistory.forEach((item) => {
              const by = String(item?.by || "").trim();
              if (by && !by.includes("@") && by.length >= 20) {
                lookupUids.add(by);
              }
            });
          }
        });
      }

      if (pendingAdminRequestsRes.status === "fulfilled") {
        pendingAdminRequestsRes.value.docs.forEach((d) => {
          const data = d.data() || {};
          if (String(data.type || "").toLowerCase() !== "pricing_update") return;
          if (data.uid) lookupUids.add(String(data.uid));
        });
      }

      const missingUids = Array.from(lookupUids).filter((uid) => uid && !directory[uid]);
      if (missingUids.length > 0) {
        const userDocs = await Promise.allSettled(
          missingUids.map((uid) => db.collection("users").doc(uid).get())
        );
        userDocs.forEach((result, index) => {
          const uid = missingUids[index];
          if (result.status !== "fulfilled") return;
          const snap = result.value;
          if (!snap.exists) return;
          directory[uid] = buildDisplayName(snap.data() || {});
        });
      }

      setUserDirectory(directory);

      const firstError = [contractsRes, usersRes, accountsRes, pendingAdminRequestsRes, adminsRes, profilesRes, timesheetsRes, invoicesRes].find((r) => r.status === "rejected");
      if (firstError) {
        setMessage(normalizeError(firstError.reason, "Some admin data could not be loaded."));
      }
    } catch (error) {
      setMessage(normalizeError(error, "Failed to load admin dashboard."));
    } finally {
      setLoading(false);
    }
  }, [adminUid, initialAdminDisplayName, initialAdminPhotoURL]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const resolveUserLabel = useCallback(
    (uid, fallback = "User") => {
      const key = String(uid || "").trim();
      if (!key) return fallback || "User";
      return userDirectory[key] || fallback || "User";
    },
    [userDirectory]
  );

  const resolveContractorName = useCallback(
    (uid) => {
      const user = contractors.find((c) => String(c.uid || "") === String(uid || ""));
      if (!user) return "Contractor";
      const displayName = String(user.displayName || "").trim();
      if (displayName) return displayName;
      const full = `${String(user.fname || "").trim()} ${String(user.lname || "").trim()}`.trim();
      return full || "Contractor";
    },
    [contractors]
  );

  const overview = useMemo(() => {
    const submitted = contracts.filter((c) => String(c.status || "").toLowerCase() === "submitted").length;
    const shortlisted = contracts.filter((c) => String(c.status || "").toLowerCase() === "shortlisted").length;
    const matched = contracts.filter((c) => String(c.status || "").toLowerCase() === "matched").length;
    const active = contracts.filter((c) => String(c.status || "").toLowerCase() === "active").length;
    const pendingPricing = pricingRequests.filter((r) => String(r.status || "pending").toLowerCase() === "pending").length;
    const pendingContractorReviews = contractors.filter(isContractorReviewPending).length;
    const openInvoiceRows = invoices.filter((row) => ["open", "approved"].includes(String(row.status || "").toLowerCase()));
    const openInvoiceAmount = openInvoiceRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const buildInvoiceSeries = (months) => buildMonthlySeries(
      invoices,
      (row) => String(row.month || ""),
      (row) => Number(row.amount || 0),
      months
    );
    const approvedInvoiceRows = invoices.filter((row) => String(row.status || "").toLowerCase() === "approved");
    const paidInvoiceRows = invoices.filter((row) => String(row.status || "").toLowerCase() === "paid");
    const approvedInvoiceAmount = approvedInvoiceRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const paidInvoiceVolume = paidInvoiceRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const contractorStatusCounts = ["submitted", "changes_requested", "approved", "rejected", "suspended"].map((status) => ({
      label: status.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      value: contractors.filter((row) => String(row.onboarding_status || row.review?.status || "").toLowerCase() === status).length,
    }));
    return {
      accounts: accountsCount,
      active,
      matched,
      summary: [
        { label: "Open requests", value: submitted + shortlisted, helper: "Client matching activity" },
        { label: "Pending reviews", value: pendingContractorReviews, helper: "Contractor approvals" },
        { label: "Open invoices", value: formatMoney(openInvoiceAmount), helper: `${openInvoiceRows.length} invoice${openInvoiceRows.length === 1 ? "" : "s"}` },
        { label: "Active contracts", value: active, helper: `${matched} matched` },
      ],
      metrics: [],
      queue: submitted + shortlisted,
      seriesByRange: {
        3: buildInvoiceSeries(3),
        6: buildInvoiceSeries(6),
        12: buildInvoiceSeries(12),
      },
      series: buildInvoiceSeries(6),
      breakdowns: {
        operations: [
          { label: "Submitted", value: submitted },
          { label: "Shortlisted", value: shortlisted },
          { label: "Matched", value: matched },
          { label: "Active", value: active },
          { label: "Pricing", value: pendingPricing },
        ],
        people: [
          ...contractorStatusCounts,
          { label: "Open timesheets", value: openTimesheets.length },
        ],
        finance: [
          { label: "Open", value: openInvoiceAmount },
          { label: "Approved", value: approvedInvoiceAmount },
          { label: "Paid", value: paidInvoiceVolume },
        ],
      },
      totalContracts: contracts.length,
      contractors: contractors.length,
    };
  }, [accountsCount, contracts, contractors, invoices, openTimesheets.length, pricingRequests]);

  const navBadges = useMemo(() => {
    const operationsCount = contracts.filter((c) => String(c.status || "").toLowerCase() !== "active").length + pricingRequests.length + openTimesheets.length;
    const financeCount = invoices.filter((row) => ["open", "approved"].includes(String(row.status || "").toLowerCase())).length;
    const pendingContractorReviews = contractors.filter(isContractorReviewPending).length;
    return {
      operations: operationsCount,
      people: pendingContractorReviews,
      finance: financeCount,
    };
  }, [contracts, contractors, invoices, openTimesheets.length, pricingRequests.length]);

  const hireRequestContracts = useMemo(
    () => contracts.filter((c) => String(c.status || "").toLowerCase() !== "active"),
    [contracts]
  );

  const manageContractRows = useMemo(
    () => contracts.filter((c) => String(c.status || "").toLowerCase() === "active"),
    [contracts]
  );

  const approvedContractors = useMemo(
    () => contractors.filter((contractor) => isContractorApproved(contractor)),
    [contractors]
  );

  const moveStatus = async (contract, nextStatus, reason, extra = {}) => {
    if (!contract?.id || !nextStatus) return;
    setBusy(contract.id, true);
    setMessage("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("adminUpdateHireRequestStatus");
      await fn({
        contract_id: contract.id,
        next_status: nextStatus,
        reason: String(reason || ""),
        contract_start_date: String(extra?.contractStartDate || ""),
        contract_end_date: String(extra?.contractEndDate || ""),
        expected_version: Number(contract.version || 0),
      });
      await loadAdminData();
    } catch (error) {
      setMessage(normalizeError(error, "Failed to update request status."));
    } finally {
      setBusy(contract.id, false);
    }
  };

  const runMatchedContractAction = async (contract, callableName, payload = {}) => {
    if (!contract?.id) return;
    setBusy(contract.id, true);
    setMessage("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable(callableName);
      await fn({
        contract_id: contract.id,
        expected_version: Number(contract.version || 0),
        ...payload,
      });
      await loadAdminData();
    } catch (error) {
      setMessage(normalizeError(error, "Failed to update contract approval."));
    } finally {
      setBusy(contract.id, false);
    }
  };

  const resolvePricing = async (req, decision, options = {}) => {
    if (!req?.id) return;
    setBusy(req.id, true);
    setMessage("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("adminResolvePricingUpdate");
      const payload = {
        request_id: req.id,
        decision,
      };
      if (options?.effectiveDate) payload.effective_date = String(options.effectiveDate);
      if (options?.reason) payload.reason = String(options.reason);
      await fn(payload);
      await loadAdminData();
    } catch (error) {
      setMessage(normalizeError(error, "Failed to resolve pricing update."));
    } finally {
      setBusy(req.id, false);
    }
  };

  const reviewContractor = async (contractor, decision, options = {}) => {
    const uid = String(contractor?.uid || contractor?.id || "").trim();
    if (!uid || !decision) return;
    setBusy(uid, true);
    setMessage("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("adminReviewContractor");
      await fn({
        uid,
        decision,
        public_message: String(options?.public_message || ""),
        notes: String(options?.notes || ""),
        interview_required: options?.interview_required === true,
        interview_status: String(options?.interview_status || ""),
      });
      await loadAdminData();
    } catch (error) {
      setMessage(normalizeError(error, "Failed to review contractor."));
    } finally {
      setBusy(uid, false);
    }
  };

  const requestContractRateChange = async (contract, payload = {}) => {
    if (!contract?.id) return;
    setBusy(contract.id, true);
    setMessage("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("adminRequestContractRateChange");
      await fn({
        contract_id: contract.id,
        requested_hourly_rate: Number(payload?.requestedHourlyRate || 0),
        requested_monthly_rate: Number(payload?.requestedMonthlyRate || 0),
        effective_date: String(payload?.effectiveDate || ""),
        reason: String(payload?.reason || ""),
      });
      await loadAdminData();
    } catch (error) {
      setMessage(normalizeError(error, "Failed to request contract rate change."));
    } finally {
      setBusy(contract.id, false);
    }
  };

  const resolveTimesheet = async (timesheet, status) => {
    if (!timesheet?.id || !timesheet?.userId || !["approved", "rejected"].includes(String(status || "").toLowerCase())) return;
    setBusy(timesheet.key, true);
    setMessage("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("adminResolveTimesheet");
      await fn({
        user_id: timesheet.userId,
        timesheet_id: timesheet.id,
        status: String(status).toLowerCase(),
      });
      await loadAdminData();
    } catch (error) {
      setMessage(normalizeError(error, "Failed to update timesheet status."));
    } finally {
      setBusy(timesheet.key, false);
    }
  };

  const moveInvoiceToProcessing = async (invoice, confirmationText, reason = "") => {
    if (!invoice?.id || !invoice?.userId) return;
    const busyKey = `${invoice.userId}:${invoice.id}`;
    setBusy(busyKey, true);
    setMessage("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("adminUpdateContractorInvoiceStatus");
      await fn({
        user_id: invoice.userId,
        invoice_id: invoice.id,
        next_status: "processing",
        confirmation_text: String(confirmationText || ""),
        reason: String(reason || ""),
      });
      await loadAdminData();
    } catch (error) {
      setMessage(normalizeError(error, "Failed to update invoice status."));
    } finally {
      setBusy(busyKey, false);
    }
  };

  const approveInvoice = async (invoice) => {
    if (!invoice?.id || !invoice?.userId) return;
    const busyKey = `${invoice.userId}:${invoice.id}`;
    setBusy(busyKey, true);
    setMessage("");
    try {
      const fn = FirebaseAuth.functions().httpsCallable("adminUpdateContractorInvoiceStatus");
      await fn({
        user_id: invoice.userId,
        invoice_id: invoice.id,
        next_status: "approved",
      });
      await loadAdminData();
    } catch (error) {
      setMessage(normalizeError(error, "Failed to approve invoice."));
    } finally {
      setBusy(busyKey, false);
    }
  };

  return {
    adminDisplayName: adminProfile.displayName,
    adminPhotoURL: adminProfile.photoURL,
    adminUid,
    busyById,
    contractorProfilesByUid,
    contractors,
    approvedContractors,
    contracts,
    finance: {
      invoices,
      onApprove: approveInvoice,
      onMoveToProcessing: moveInvoiceToProcessing,
    },
    formatCurrency,
    formatDate,
    hireRequestContracts,
    loading,
    manageContractRows,
    message,
    navBadges,
    operations: {
      onMoveStatus: moveStatus,
      onApproveMatchedContract: (contract) => runMatchedContractAction(contract, "adminApproveMatchedContract"),
      onReturnMatchedContract: (contract, reason) => runMatchedContractAction(contract, "adminReturnMatchedContract", { reason: String(reason || "") }),
      onConfirmExternalSignatures: (contract) => runMatchedContractAction(contract, "adminConfirmExternalContractSignatures", {
        ops51_signed: true,
        contractor_signed: true,
      }),
      onRequestRateChange: requestContractRateChange,
      onResolveTimesheet: resolveTimesheet,
      openTimesheets,
      pricingRequests,
      resolvePricing,
    },
    overview,
    people: {
      onReviewContractor: reviewContractor,
    },
    resolveContractorName,
    resolveUserLabel,
    statusColor,
  };
};

export default useAdminDashboard;
